import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Library, Loader2, Plus, Check, FileImage, CalendarClock, Landmark, MapPin } from 'lucide-react';

function TemplateCard({ t, canAdd, onAdd, adding }) {
  const navigate = useNavigate();
  const federal = t.jurisdiction === 'federal';
  return (
    <Card className="careiq-card hover:border-[#0E7490]/40 transition-colors" data-testid={`library-item-${t.key}`}>
      <CardContent className="p-5 flex flex-col h-full gap-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-[#0A2342] leading-snug">{t.name}</h3>
          <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${federal ? 'bg-[#0A2342]/10 text-[#0A2342]' : 'bg-[#0E7490]/10 text-[#0E7490]'}`} data-testid={`library-jurisdiction-${t.key}`}>
            {federal ? <Landmark className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}{federal ? 'Federal' : t.jurisdiction}
          </span>
        </div>
        <p className="text-xs text-[#64748B] flex-1">{t.description}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#64748B]">
          {t.print_layout === 'pdf_original' && <span className="flex items-center gap-1"><FileImage className="w-3 h-3" />Original layout · {t.page_count} page{t.page_count > 1 ? 's' : ''}</span>}
          <span>{t.field_count} fields</span>
          {t.update_interval_days ? <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" />Every {t.update_interval_days} days</span> : null}
        </div>
        {t.source && <p className="text-[10px] text-[#94A3B8] italic">{t.source}</p>}
        {t.added_form_id ? (
          <Button variant="outline" size="sm" onClick={() => navigate(`/forms/builder?id=${t.added_form_id}`)} data-testid={`library-open-${t.key}`}><Check className="w-4 h-4 mr-1 text-[#166534]" />In your forms — open</Button>
        ) : canAdd ? (
          <Button size="sm" className="careiq-btn-primary" onClick={() => onAdd(t)} disabled={adding === t.key} data-testid={`library-add-${t.key}`}>
            {adding === t.key ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Add to my forms
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function FormLibraryPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [state, setState] = useState(null);
  const [adding, setAdding] = useState(null);
  const canAdd = ['super_admin', 'practice_admin'].includes(user?.role);

  const load = (st) => api.get('/form-library', { params: st ? { state: st } : {} }).then(r => { setData(r.data); setState(r.data.state); }).catch(() => setData({ items: [], states: [] }));
  useEffect(() => { load(null); }, []);

  const add = async (t) => {
    setAdding(t.key);
    try {
      await api.post(`/form-library/${t.key}/add`);
      toast.success(`"${t.name}" added to your forms`);
      load(state);
    } catch (e) { toast.error(String(e?.response?.data?.detail || 'Could not add').split('|')[0]); }
    finally { setAdding(null); }
  };

  return (
    <div className="space-y-6" data-testid="form-library-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Library className="w-7 h-7 text-[#0E7490]" />Form Library</h1>
          <p className="text-[#64748B] mt-1 max-w-2xl">Ready-made home care forms. Federal forms apply everywhere; state forms show for the state you pick. Added forms pre-fill from the patient record and keep their update schedule.</p>
        </div>
        {data && (
          <Select value={state || 'ALL'} onValueChange={(v) => load(v)}>
            <SelectTrigger className="w-72" data-testid="library-state-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="ALL">All jurisdictions</SelectItem>
              <SelectItem value="FEDERAL">Federal only</SelectItem>
              {(data.states || []).map(s => <SelectItem key={s} value={s}>{s}{s === data.org_state ? ' (your agency)' : ''} + Federal</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {data && !data.org_state && canAdd && (
        <p className="text-xs text-[#92400E] bg-[#FEF3C7]/60 border border-[#CA8A04]/20 rounded-lg px-3 py-2" data-testid="library-no-state-hint">Tip: set your agency's state under Forms → Agency Details so this list filters to your state automatically.</p>
      )}
      {!data ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /></div> : data.items.length === 0 ? (
        <p className="text-sm text-[#94A3B8] py-12 text-center" data-testid="library-empty">No templates for this jurisdiction yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.items.map(t => <TemplateCard key={t.key} t={t} canAdd={canAdd} onAdd={add} adding={adding} />)}
        </div>
      )}
    </div>
  );
}
